import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import LLMConfigurationEditModal from "./LLMConfigurationEditModal";
import { getLLMConfigurations, deleteLLMConfiguration, setDefaultLLMConfiguration } from "../../api";

interface LLMConfiguration {
    id: number;
    name: string;
    provider: string;
    model_id: string;
    api_url?: string;
    api_key_set: boolean;
    temperature: number;
    max_tokens: number;
    request_timeout: number;
    is_default: boolean;
    created_at: string;
    updated_at: string;
}

interface LLMConfigurationsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const LLMConfigurationsModal: React.FC<LLMConfigurationsModalProps> = ({
    isOpen,
    onClose,
}) => {
    const { t } = useTranslation();
    const [configurations, setConfigurations] = useState<LLMConfiguration[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingConfig, setEditingConfig] = useState<LLMConfiguration | null>(null);

    const fetchConfigurations = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await getLLMConfigurations();
            setConfigurations(response.configurations);
        } catch (err) {
            
            setError(t('errors.failedToLoadLlmConfigurations'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        if (isOpen) {
            fetchConfigurations();
        }
    }, [isOpen, fetchConfigurations]);

    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === "Escape" && isOpen && !isEditModalOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscapeKey);
        }

        return () => {
            document.removeEventListener("keydown", handleEscapeKey);
        };
    }, [isOpen, isEditModalOpen, onClose]);

    const handleEdit = (config: LLMConfiguration) => {
        setEditingConfig(config);
        setIsEditModalOpen(true);
    };

    const handleAdd = () => {
        setEditingConfig(null);
        setIsEditModalOpen(true);
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(t('llm.configurations.deleteConfirmation'))) {
            return;
        }

        try {
            await deleteLLMConfiguration(id);
            // Refresh the list
            fetchConfigurations();
        } catch (err) {
            
            setError(t('errors.failedToDeleteConfiguration'));
        }
    };

    const handleSetDefault = async (id: number) => {
        try {
            await setDefaultLLMConfiguration(id);
            // Refresh the list
            fetchConfigurations();
        } catch (err) {
            
            setError(t('errors.failedToSetDefaultConfiguration'));
        }
    };

    const handleEditClose = () => {
        setIsEditModalOpen(false);
        setEditingConfig(null);
        // Refresh configurations when edit modal closes
        fetchConfigurations();
    };

    const getProviderDisplayName = (provider: string) => {
        switch (provider) {
            case 'openrouter':
                return t('llm.configurations.providerOpenRouter');
            case 'ollama':
                return t('llm.configurations.providerOllama');
            case 'openai_compatible':
                return t('llm.configurations.providerOpenAI');
            default:
                return provider;
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 bg-gray-600 dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50">
                <div className="relative top-20 mx-auto p-5 border border-gray-200 dark:border-gray-700 w-[800px] shadow-lg rounded-md bg-white dark:bg-gray-800">
                    <div className="mt-3">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                                {t('llm.configurations.title')}
                            </h3>
                            <button
                                onClick={handleAdd}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                            >
                                {t('llm.configurations.addConfiguration')}
                            </button>
                        </div>

                        {error && (
                            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-200 rounded">
                                {error}
                            </div>
                        )}

                        {loading ? (
                            <div className="flex justify-center items-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {configurations.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                        <p className="text-lg mb-2">{t('llm.configurations.noConfigurationsFound')}</p>
                                        <p className="text-sm">{t('llm.configurations.noConfigurationsDescription')}</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {configurations.map((config) => (
                                            <div
                                                key={config.id}
                                                className={`p-4 border rounded-lg ${
                                                    config.is_default
                                                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                                        : 'border-gray-200 dark:border-gray-600'
                                                }`}
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                                                                {config.name}
                                                            </h4>
                                                            {config.is_default && (
                                                                <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100 rounded-full">
                                                                    {t('llm.configurations.default')}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-400">
                                                            <div>
                                                                <span className="font-medium">{t('llm.configurations.provider')}:</span> {getProviderDisplayName(config.provider)}
                                                            </div>
                                                            <div>
                                                                <span className="font-medium">{t('llm.configurations.modelId')}:</span> {config.model_id}
                                                            </div>
                                                            <div>
                                                                <span className="font-medium">{t('llm.temperature')}:</span> {config.temperature}
                                                            </div>
                                                            <div>
                                                                <span className="font-medium">{t('llm.maxTokens')}:</span> {config.max_tokens}
                                                            </div>
                                                            <div>
                                                                <span className="font-medium">{t('llm.configurations.apiKey')}:</span> {config.api_key_set ? t('llm.configurations.apiKeySet') : t('llm.configurations.apiKeyNotSet')}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2 ml-4">
                                                        {!config.is_default && (
                                                            <button
                                                                onClick={() => handleSetDefault(config.id)}
                                                                className="px-3 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                                            >
                                                                {t('llm.configurations.setDefault')}
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => handleEdit(config)}
                                                            className="px-3 py-1 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"
                                                        >
                                                            {t('common.edit')}
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(config.id)}
                                                            className="px-3 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                                        >
                                                            {t('common.delete')}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
                            >
                                {t('common.close')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Render the edit modal if it's open */}
            <LLMConfigurationEditModal
                isOpen={isEditModalOpen}
                onClose={handleEditClose}
                configuration={editingConfig}
            />
        </>
    );
};

export default LLMConfigurationsModal;